<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  shouldShowReplyModeToggle: {
    type: Boolean,
    default: () => true,
  },
});

const emit = defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => props.mode === REPLY_EDITOR_MODES.NOTE);

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;
  return `${widthToUse + 16}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  return isPrivate.value ? `${replyModeSize.width.value + 16}px` : '0px';
});

const containerClasses = computed(() => {
  const baseClasses =
    'flex items-center w-auto h-8 p-1 transition-all border rounded-full relative duration-300 ease-in-out z-0 bg-n-alpha-2';

  if (props.shouldShowReplyModeToggle) {
    return `${baseClasses} cursor-pointer`;
  }

  return `${baseClasses} cursor-default`;
});

const onToggle = () => {
  if (props.shouldShowReplyModeToggle) {
    emit('toggleMode');
  }
};
</script>

<template>
  <button :class="containerClasses" @click="onToggle">
    <div
      v-if="shouldShowReplyModeToggle || !isPrivate"
      ref="wootEditorReplyMode"
      class="flex items-center gap-1 px-2 z-20"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>

    <div
      v-if="shouldShowReplyModeToggle || isPrivate"
      ref="wootEditorPrivateMode"
      class="flex items-center gap-1 px-2 z-20"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>

    <div
      v-if="shouldShowReplyModeToggle"
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] transition-all duration-300 ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
